import React from 'react';
import {motion} from 'framer-motion';
import * as FaIcons from 'react-icons/fa';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const {FaCar,FaCheckCircle} = FaIcons;
const {FiAlertCircle,FiPhone,FiMessageCircle,FiExternalLink} = FiIcons;

const santosh = {
  name: 'Santosh',
  role: 'Pre-Test Preparation Specialist',
  image: 'https://media-manager-c.questera.ai/greta-media/0b3af998e770acea81dd65e1f88cbe23e5433846e0d8905ff7ba265c9308956ef02252169608e8dd98a402f9c5748de4/images/aW1hZ2UvcG5n/07dec7ffc458e38acecbbfb12ac7d20c.png',
  description: 'A calm, experienced instructor specialising in helping test-ready learners and overseas licence holders prepare confidently for their NSW driving test.',
  credentials: ['Cert IV Driving Instruction', 'Licensed Since 2021', 'Automatic Specialist', 'Goulburn Road Knowledge'],
  vehicle: 'Sessions use the student’s own registered and roadworthy automatic vehicle.'
};

const milan = {
  name: 'Milan',
  role: 'RSS Driving School instructor',
  image: 'https://media-manager-c.questera.ai/greta-media/0b3af998e770acea81dd65e1f88cbe23e5433846e0d8905ff7ba265c9308956ef02252169608e8dd98a402f9c5748de4/images/aW1hZ2UvanBlZw==/3711179b29c65b1c539771af9787d433.jpeg',
  description: 'Milan operates as RSS Driving School and offers calm, supportive automatic driving instruction for learners at different stages.',
  credentials: ['New Students Welcome', 'Test-Ready Students Welcome', 'Calm & Supportive', 'Automatic-Only Instruction'],
  vehicle: 'RSS Driving School lessons are provided in an automatic dual-control vehicle.'
};

const rssBanner = 'https://media-manager-c.questera.ai/greta-media/0b3af998e770acea81dd65e1f88cbe23e5433846e0d8905ff7ba265c9308956ef02252169608e8dd98a402f9c5748de4/images/aW1hZ2UvanBlZw==/bccc6e8a7562266051b192ed87bc37c9.jpg';

function InstructorCard({instructor, external = false}) {
  return (
    <motion.article initial={{opacity: 0, y: 35}} whileInView={{opacity: 1, y: 0}} viewport={{once: true}} className="mx-auto flex w-full max-w-xl flex-col overflow-hidden rounded-3xl border border-white/10 bg-brand-surface shadow-2xl">
      <div className="relative aspect-[4/3] overflow-hidden bg-brand-navy">
        <img src={instructor.image} alt={`${instructor.name}, driving instructor`} className={`h-full w-full object-cover ${external ? 'object-top' : 'object-center'}`} loading="lazy" />
        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-brand-navy via-brand-navy/70 to-transparent px-6 pb-5 pt-16">
          <h3 className="font-display text-3xl text-white">{instructor.name}</h3>
          <p className="font-bold text-brand-gold">{instructor.role}</p>
        </div>
      </div>
      <div className="flex flex-1 flex-col p-6 sm:p-7">
        <p className="mb-6 leading-relaxed text-white/75">{instructor.description}</p>
        <div className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
          {instructor.credentials.map((credential) => (
            <div key={credential} className="flex items-center gap-2 rounded-lg border border-white/10 bg-white/5 p-3 text-sm font-semibold text-white">
              <SafeIcon icon={FaCheckCircle} className="shrink-0 text-brand-green" />
              <span>{credential}</span>
            </div>
          ))}
        </div>
        <div className="mb-5 flex items-start gap-3 rounded-xl bg-brand-red p-4 text-white">
          <SafeIcon icon={FaCar} className="mt-0.5 shrink-0 text-xl" />
          <p className="text-sm leading-relaxed">{instructor.vehicle}</p>
        </div>
        {external ? (
          <>
            <p className="mb-5 rounded-xl border border-brand-green/30 bg-brand-green/10 p-4 text-center font-bold text-brand-green">RSS Driving School welcomes new and test-ready students. Contact Milan directly for availability and bookings.</p>
            <div className="mt-auto grid gap-3 sm:grid-cols-2">
              <a href="tel:0433037815" className="flex min-h-12 items-center justify-center gap-2 rounded-xl bg-brand-gold px-4 py-3 font-bold text-brand-navy transition-colors hover:bg-[#D4891A]">
                <SafeIcon icon={FiPhone} /> Call Milan
              </a>
              <a href="sms:0433037815" className="flex min-h-12 items-center justify-center gap-2 rounded-xl border border-brand-gold px-4 py-3 font-bold text-brand-gold transition-colors hover:bg-brand-gold hover:text-brand-navy">
                <SafeIcon icon={FiMessageCircle} /> Text Milan
              </a>
            </div>
            <p className="mt-4 text-center font-semibold text-white">0433 037 815</p>
          </>
        ) : (
          <div className="mb-1 flex items-start gap-3 rounded-xl border border-brand-red/40 bg-brand-red/15 p-4 text-white">
            <SafeIcon icon={FiAlertCircle} className="mt-0.5 shrink-0 text-xl text-brand-red" />
            <p className="text-sm font-semibold leading-relaxed">Very limited spots: Currently taking test-ready students only. Beginner drivers, please do not book.</p>
          </div>
        )}
      </div>
    </motion.article>
  );
}

export default function Instructor() {
  return (
    <div id="instructors" className="scroll-mt-28 bg-brand-darkbg">
      <section className="py-24">
        <div className="container mx-auto px-4 md:px-6">
          <div className="mx-auto mb-14 max-w-3xl text-center">
            <p className="mb-3 font-bold uppercase tracking-[0.2em] text-brand-red">Drive Pal Driving School</p>
            <h2 className="mb-5 font-display text-4xl text-white md:text-6xl">Meet Your <span className="text-brand-gold">Instructor</span></h2>
            <p className="text-lg leading-relaxed text-white/70">Focused pre-test preparation in Goulburn with Santosh.</p>
          </div>
          <InstructorCard instructor={santosh} />
        </div>
      </section>
      <section className="border-t border-white/10 bg-brand-navy/30 py-20">
        <div className="container mx-auto px-4 md:px-6">
          <div className="mx-auto mb-10 max-w-3xl text-center">
            <p className="mb-3 font-bold uppercase tracking-[0.2em] text-brand-gold">Other Instructor You Can Contact</p>
            <h2 className="mb-4 font-display text-4xl text-white md:text-5xl">Milan at <span className="text-brand-gold">RSS Driving School</span></h2>
            <p className="text-white/70">RSS Driving School operates separately from Drive Pal. Please contact Milan directly to arrange a lesson with him.</p>
          </div>
          <div className="mx-auto mb-8 max-w-3xl overflow-hidden rounded-2xl border border-white/10 bg-white shadow-xl">
            <img src={rssBanner} alt="RSS Driving School banner featuring learner vehicle and contact number 0433 037 815" className="w-full" loading="lazy" />
          </div>
          <InstructorCard instructor={milan} external />
          <p className="mx-auto mt-6 max-w-xl text-center text-sm text-white/55">
            Milan’s RSS Driving School bookings are arranged directly by phone or text, not through Drive Pal’s online booking calendar.
          </p>
        </div>
      </section>
    </div>
  );
}